import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Clock, Calendar, Loader2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { format } from "date-fns";
import { sanitizeArticleHTML } from "@/lib/sanitize";
import ClinicalSourcesBadge from "@/components/ClinicalSourcesBadge";
import SEO from "@/components/SEO";

interface Article {
  id: string;
  title: string;
  content: string;
  category: string;
  author_name: string;
  read_time: number;
  featured_image_url: string;
  published_at: string;
  sources?: unknown;
  clinical_grade?: string | null;
  reviewed_at?: string | null;
  reviewed_by?: string | null;
  next_review_at?: string | null;
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('articles')
        .select('*')
        .eq('slug', slug)
        .single();

      if (fetchError) throw fetchError;
      if (!data) throw new Error('Article not found');

      setArticle(data);
    } catch (err) {
      console.error('Error fetching article:', err);
      setError('Failed to load article. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
        <div className="container mx-auto px-4 py-8">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {error || 'Article not found'}
              <Link to="/all-the-goss">
                <Button variant="outline" size="sm" className="ml-4">
                  Back to Articles
                </Button>
              </Link>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const plainDesc = article.content
    ? article.content.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 160)
    : `${article.title} — read this ${article.category} article on Her Season.`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <SEO
        title={article.title}
        description={plainDesc}
        ogType="article"
        image={article.featured_image_url || undefined}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          image: article.featured_image_url ? [article.featured_image_url] : undefined,
          datePublished: article.published_at,
          author: { "@type": "Person", name: article.author_name },
          publisher: { "@type": "Organization", name: "Her Season" },
          articleSection: article.category,
        }}
      />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Link to="/all-the-goss">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to All the Goss
          </Button>
        </Link>

        <article>
          {/* Hero Image */}
          {article.featured_image_url && (
            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.featured_image_url}
                alt={article.title}
                className="w-full h-[400px] object-cover"
              />
            </div>
          )}

          {/* Article Header */}
          <div className="mb-8">
            <Badge variant="outline" className="mb-4 capitalize font-sans">
              {article.category}
            </Badge>
            <h1 className="text-4xl md:text-5xl font-headline font-bold mb-4 leading-tight text-foreground">
              {article.title}
            </h1>
            <div className="flex items-center gap-6 text-muted-foreground text-sm font-serif">
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {format(new Date(article.published_at), 'MMMM d, yyyy')}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {article.read_time} min read
              </span>
              <span>By {article.author_name}</span>
            </div>
            <div className="mt-6">
              <ClinicalSourcesBadge
                sources={article.sources}
                clinical_grade={article.clinical_grade}
                reviewed_at={article.reviewed_at}
                reviewed_by={article.reviewed_by}
                next_review_at={article.next_review_at}
              />
            </div>
          </div>


          {/* Article Content */}
          <Card className="p-8 md:p-12 border-none shadow-none">
            <div 
              className="prose prose-lg max-w-none dark:prose-invert font-serif
                prose-headings:font-headline prose-headings:text-foreground
                prose-p:text-foreground prose-p:leading-relaxed
                prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                prose-strong:text-foreground prose-strong:font-semibold
                prose-ul:text-foreground prose-li:text-foreground"
              // Article HTML is admin-authored but sanitized as defence-in-depth
              // against compromised admin accounts. Do not remove sanitizeArticleHTML.
              dangerouslySetInnerHTML={{ __html: sanitizeArticleHTML(article.content) }}
            />
          </Card>
        </article>
      </div>
    </div>
  );
}
